export const CHECKOUT_URL = "https://blepharoplasty-en.impultienda.ar/checkout/";

export const images = {
  mainMockup: "https://cdn.magicpatterns.com/uploads/nwY2xRYXyxSM4fbuvn8s3t/BLEPHAROPLASTY-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/1kgrN9xtT52ZkURU7R3XDa/Daniel_Reynolds_-_Blefaroplastia.png",
};

export const organizeItems = [
  "Patient objectives.",
  "Visual observations.",
  "Relevant variables.",
  "Questions.",
  "Information gaps.",
  "Context.",
];

export const pathwaySteps = [
  { name: "Assess", prompts: ["What information do you have?", "What information is missing?", "What are the patient’s stated objectives?"] },
  { name: "Observe", prompts: ["What do you actually see?", "What deserves closer review?"] },
  { name: "Organize", prompts: ["Where do your observations belong?"] },
  { name: "Question", prompts: ["What would you want to clarify?"] },
  { name: "Prioritize", prompts: ["Which observations deserve additional attention?"] },
  { name: "Review", prompts: ["How does your initial reasoning compare with the educational review?"] },
  { name: "Learn", prompts: ["What did you notice?", "What did you miss?", "What will you review again?"] },
];

export const pains = [
  { icon: "compass", title: "“Where do I even start?”", text: "Multiple variables compete for your attention." },
  { icon: "clock", title: "“I need to review everything before I can organize my thoughts.”", text: "You move between articles, notes, lectures and previous cases." },
  { icon: "shuffle", title: "“Am I looking at the case in the right order?”", text: "Without a repeatable framework, every case can feel different." },
  { icon: "search", title: "“What am I missing?”", text: "You notice certain features but want a more structured way to identify information gaps." },
  { icon: "book", title: "“I keep learning, but I need more practice analyzing cases.”", text: "More information doesn’t automatically mean more opportunities to practice your reasoning." },
  { icon: "brain", title: "“I want to learn something from every case.”", text: "But without structured reflection, valuable observations can disappear after the review." },
] as const;

export const volumes = [
  {
    number: "I",
    title: "The 15-Minute Blepharoplasty Assessment Pathway™",
    intro: "I wanted the first volume to answer one simple question: “Where do I start?”",
    body: "You’ll receive the complete educational pathway I designed to organize your case review — Assess → Observe → Organize → Question → Prioritize → Review → Learn — plus the 15-Minute Case Analysis Sheet so you can practice the framework repeatedly.",
    items: [] as string[],
  },
  {
    number: "II",
    title: "The Upper Eyelid Assessment Map™",
    intro: "When you’re reviewing an upper-eyelid case, it’s easy to let individual observations compete for your attention. This volume gives you a structured way to organize them.",
    body: "My goal here is simple: give you a better structure for looking at the case.",
    items: ["Patient objectives", "Available case information", "Visual observations", "Relevant contextual factors", "Information gaps", "Questions requiring further review", "Documentation prompts", "Reflection points"],
  },
  {
    number: "III",
    title: "The Lower Eyelid Assessment Map™",
    intro: "Rather than jumping immediately from observation to conclusion, you’ll work through a consistent educational review process.",
    body: "This is not a universal surgical recipe. It’s a framework for organizing your professional thinking.",
    items: ["Patient objectives", "Case context", "Visual assessment", "Relevant variables", "Questions to investigate", "Areas requiring additional review", "Documentation", "Post-case reflection"],
  },
  {
    number: "V",
    title: "The Difficult Case & Review Pathway™",
    intro: "Some cases deserve more thought than others. For these educational scenarios, I’ve created a structured review process.",
    body: "I don’t want you to simply memorize “difficult cases.” I want you to practice recognizing when a case deserves deeper review.",
    items: ["What information is available?", "What appears uncertain?", "What deserves additional review?", "What questions should be considered?", "What observations could easily be overlooked?", "What should be documented?", "What can be learned from the case?"],
  },
  {
    number: "VI",
    title: "The Blepharoplasty Case Review System™",
    intro: "A case shouldn’t stop being useful when the review ends. You’ll receive templates to build your own educational case-learning archive.",
    body: "Over time, you aren’t just reviewing cases. You’re building a record of how your thinking develops.",
    items: ["Case documentation", "Case reflection", "Case comparison", "Learning points", "Knowledge gaps", "Questions for future review", "Monthly case review", "Progress tracking", "Annual professional reflection"],
  },
];

export const atlasSteps = [
  "Review the case.",
  "Write down what you notice.",
  "Identify the variables you consider relevant.",
  "Identify questions and information gaps.",
  "Prioritize what deserves further review.",
  "Compare your reasoning with the educational review.",
  "Record what you learned.",
];

export const tools = [
  { icon: "sheet", title: "The 15-Minute Case Analysis Sheet", text: "The one-page version of the complete pathway." },
  { icon: "checklist", title: "The Pre-Case Review Checklist", text: "The questions I recommend reviewing before beginning an educational case analysis." },
  { icon: "eye", title: "The Eyelid Assessment Map", text: "A visual framework for organizing observations." },
  { icon: "matrix", title: "The Case Comparison Matrix", text: "A simple way to compare two educational cases side by side." },
  { icon: "camera", title: "The Visual Case Documentation Guide", text: "A structured framework for organizing visual case documentation for educational review." },
] as const;

export const bonuses = [
  {
    number: 1,
    title: "30 Educational Blepharoplasty Cases",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/axErGreuggidHYj93sUrMx/BONUS_1_BLEPHAROPLASTY-EN.png",
    text: "I wanted you to have more opportunities to practice the pathway. You’ll receive 30 additional educational cases designed around the same process: Case → Think → Document → Review → Learn.",
  },
  {
    number: 2,
    title: "The 10-Minute Case Review™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/nF12NoRd1JZCczn3cWLLXt/BONUS_2_BLEPHAROPLASTY-EN.png",
    text: "A compact version for those moments when you don’t have time to complete the full pathway. 10 minutes. One case. One structured review.",
  },
  {
    number: 3,
    title: "The Blepharoplasty Error & Oversight Atlas™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/3ar9H7trc9T8m6rbteveMf/BONUS_3_BLEPHAROPLASTY-EN.png",
    text: "Educational scenarios focused on things that can be overlooked during case analysis: what happened, what may have been overlooked, which questions to consider, and the lesson to extract. It’s not about memorizing mistakes — it’s training you to look for what you might otherwise miss.",
  },
  {
    number: 4,
    title: "The Blepharoplasty Case Review Cards™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/gtfNLLZqM8utaY5vYz8eFi/BONUS_4_BLEPHAROPLASTY-EN.png",
    text: "Printable case-review prompts you can use individually, with colleagues or during educational case discussions. One card. One question. One focused review.",
  },
  {
    number: 5,
    title: "The 30-Day Blepharoplasty Case Analysis Challenge™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/r1nbruznWj5Tif7Yx2N2Ai/BONUS_5_BLEPHAROPLASTY-EN.png",
    text: "Turn case review into a consistent habit. Every day: one case, one exercise, one reflection, one lesson.",
  },
];

export const beforeItems = [
  "You look at a new case and don’t know where to start.",
  "Every case feels like a completely different problem.",
  "Your case notes are inconsistent.",
  "You jump between different sources before organizing your thoughts.",
  "You finish a case without systematically extracting what you learned.",
];

export const afterItems = [
  "You have a repeatable case-review pathway.",
  "You can work through an educational case in approximately 15 minutes.",
  "You have standardized worksheets for your case reviews.",
  "You can compare cases using the same framework.",
  "You can identify information gaps and questions for further review.",
  "You can document what you learned from each case.",
];

export const guaranteeItems = [
  "Open all six volumes",
  "Review the complete pathway",
  "Work through educational cases",
  "Download the worksheets",
  "Use the case-review tools",
  "Explore all five bonuses",
];

export const testimonials = [
  {
    name: "Dr. Elena Rodríguez",
    role: "Plastic Surgeon",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/67df36e7-add6-4f12-b99c-a8bde34b40e3.jpg",
    quote: "What I value most is the structure. Instead of opening a case and immediately looking for the answer, the pathway forces me to explain what I actually see first.",
  },
  {
    name: "Dr. Richard Hale",
    role: "Aesthetic Physician",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/f2936ca2-9b4f-4cb8-9821-affc8089769b.jpg",
    quote: "The 15-minute framework made case review much more deliberate. I now have a consistent process instead of jumping between different notes and resources.",
  },
  {
    name: "Dr. Mei Tanaka",
    role: "Oculoplastic Surgery Fellow",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/68f6ab37-ca46-4f87-8bc6-4aaf7021b903.jpg",
    quote: "The case atlas is what I use most. Writing down my reasoning before reading the review makes me notice things I previously overlooked.",
  },
  {
    name: "Dr. Marcus Bennett",
    role: "Facial Plastic Surgeon",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/fafdc19d-5fd9-41f5-b08e-66dc213af756.jpg",
    quote: "It doesn’t feel like another textbook. It feels like a tool I can actually use with a case.",
  },
  {
    name: "Dr. Claire Whitman",
    role: "Aesthetic Medicine Educator",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/7891613c-4967-45cc-8421-270e83766103.jpg",
    quote: "The comparison matrix is particularly useful for case discussions. It gives everyone a common framework for talking about the case.",
  },
];

export const faqs = [
  { q: "How will I receive Blepharoplasty Clinical Pathway™?", a: "Immediately after your purchase, you’ll receive access to the digital materials and downloadable resources included in the product." },
  { q: "Who is Blepharoplasty Clinical Pathway™ for?", a: "I designed it primarily for qualified plastic surgeons, aesthetic physicians and appropriately trained medical professionals who want a structured educational framework for reviewing blepharoplasty cases." },
  { q: "Is this a surgical technique course?", a: "No. This is a case-based educational and clinical reasoning system. It focuses on organizing observations, questions, information gaps, priorities and reflection. It does not replace formal surgical education or supervised training." },
  { q: "Does the pathway tell me exactly what to do for every patient?", a: "No. Every patient and clinical situation is different. The system is designed to help you structure your educational case assessment and review, not to provide individualized treatment instructions or a universal surgical protocol." },
  { q: "How long does each case take?", a: "The core pathway is designed around an approximately 15-minute educational case review. More complex educational cases may naturally require additional time." },
  { q: "Can I print the materials?", a: "Yes. The worksheets, matrices, checklists and case-review cards are designed to be downloaded and printed." },
  { q: "Is this a one-time payment?", a: "Yes. The launch offer is a one-time payment of $27 with lifetime digital access to the included materials." },
  { q: "Do I need to be a specialist?", a: "The system is intended for qualified medical professionals with appropriate training and experience relevant to the subject matter." },
  { q: "What happens if I don’t find it useful?", a: "You have a 7-day guarantee. Explore the materials, review the pathway and decide whether it provides value for your professional learning. If it doesn’t, you can request a refund according to the purchase terms." },
];

export const recentBuyers = [
  { name: "Sofía M.", place: "Bogotá, Colombia" },
  { name: "Alejandro R.", place: "Mexico City, Mexico" },
  { name: "Valentina G.", place: "Buenos Aires, Argentina" },
  { name: "Carlos P.", place: "Lima, Peru" },
  { name: "Lucía F.", place: "Madrid, Spain" },
  { name: "Mateo S.", place: "Santiago, Chile" },
  { name: "Camila V.", place: "Quito, Ecuador" },
  { name: "Diego H.", place: "Montevideo, Uruguay" },
  { name: "Isabella C.", place: "San José, Costa Rica" },
  { name: "Andrés L.", place: "Caracas, Venezuela" },
  { name: "Gabriela T.", place: "Panama City, Panama" },
  { name: "Javier N.", place: "Santo Domingo, Dominican Republic" },
];
